import { useCallback, useEffect, useMemo, useState } from 'react';
import PosLandingNav from './PosLandingNav';
import PosLandingHero from './PosLandingHero';
import PosLandingProof from './PosLandingProof';
import PosLandingShowcase from './PosLandingShowcase';
import PosLandingCustom from './PosLandingCustom';
import PosLandingPricing from './PosLandingPricing';
import PosLandingFaq from './PosLandingFaq';
import PosLandingFinal from './PosLandingFinal';
import PosLandingFooter from './PosLandingFooter';
import PosLandingSubscribeModal from './PosLandingSubscribeModal';
import {
  DEFAULT_ADMIN_SETTINGS, subscribeAdminSettings, subscribeSubscriptionPlans,
} from '../utils/posSubscriptionUtil';
import {
  bestSavingsPlan, entryPlan, landingPlans, landingTrialPlan, pricingTogglePlans, trialLengthLabel,
} from '../utils/posLandingUtil';
import { LANDING_DOCUMENT_TITLE } from '../constants/posLandingConst';
import '../css/PosLanding.css';

const NO_ADDONS = { extraStaff: 0, warehouse: 0 };

/**
 * The public front door at / — the Kawnter POS landing page (the approved
 * design in pos-landing.html, ported section by section).
 *
 * What an administrator controls is read live rather than written into the
 * page: the plans and their prices, inclusions and wording come from
 * /admin -> Subscription Plans, the trial card and its length from the
 * free-trial plan,
 * and the add-on prices from Admin Settings (the payment QR and account name
 * are read by the checkout tab, PosLandingPayment).
 *
 * Subscribing and booking a demo call save a sign-up request that an
 * administrator works through on /admin -> Sign-up Requests.
 *
 * Staff reach a real counter at /<client-id>; administrators manage the
 * estate at /admin, which is deliberately NOT linked from here.
 *
 * @returns {JSX.Element}
 */
function PosWelcomePage() {
  const [allPlans, setAllPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_ADMIN_SETTINGS);
  // Selected by id, not position: the trial sits in front of the paid plans,
  // and the list is live, so a position would drift as plans change.
  const [activePlanId, setActivePlanId] = useState(null);
  const [addons, setAddons] = useState(NO_ADDONS);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => subscribeSubscriptionPlans(
    (list) => { setAllPlans(list); setPlansLoading(false); setPlansError(null); },
    (error) => { setPlansError(error); setPlansLoading(false); },
  ), []);

  // Falls back to the defaults on a failed read: the page still works, it
  // just quotes the default add-on prices.
  useEffect(() => subscribeAdminSettings(setSettings, () => setSettings(DEFAULT_ADMIN_SETTINGS)), []);

  // Page-level settings that belong to this page only, put back when the
  // visitor moves on to a till or /admin:
  //  - the tab title;
  //  - smooth scrolling for the in-page links (#pricing, #faq…);
  //  - overflow-x `clip` instead of index.css's `hidden` on html and body.
  //    `hidden` turns body into a scroll container, and a sticky element
  //    then sticks to body — which never scrolls — so the nav would scroll
  //    away. `clip` hides sideways overflow the same way without that.
  useEffect(() => {
    const previousTitle = document.title;
    const { documentElement: root, body } = document;
    const previous = {
      scroll: root.style.scrollBehavior,
      rootOverflow: root.style.overflowX,
      bodyOverflow: body.style.overflowX,
    };
    document.title = LANDING_DOCUMENT_TITLE;
    root.style.scrollBehavior = 'smooth';
    root.style.overflowX = 'clip';
    body.style.overflowX = 'clip';
    return () => {
      document.title = previousTitle;
      root.style.scrollBehavior = previous.scroll;
      root.style.overflowX = previous.rootOverflow;
      body.style.overflowX = previous.bodyOverflow;
    };
  }, []);

  const plans = useMemo(() => landingPlans(allPlans), [allPlans]);
  const togglePlans = useMemo(() => pricingTogglePlans(allPlans), [allPlans]);
  const trialPlan = landingTrialPlan(allPlans);
  const trialLabel = trialLengthLabel(trialPlan);
  const bestSaving = useMemo(() => bestSavingsPlan(plans), [plans]);
  const prices = {
    extraStaffPrice: Number(settings.extraStaffPrice) || 0,
    warehousePrice: Number(settings.warehousePrice) || 0,
  };

  // Until a visitor picks, the card opens on the first PAID plan, so the price
  // is the first thing they see. A plan removed while the page is open falls
  // back the same way rather than leaving nothing selected.
  const activePlan = togglePlans.find((entry) => entry.id === activePlanId)
    || plans[0] || togglePlans[0] || null;
  const activeIndex = Math.max(0, togglePlans.indexOf(activePlan));

  /**
   * Selects a plan on the pricing card (the hero's "Save N%" link and the
   * "Try Kawnter Free" buttons know the plan, not its position).
   *
   * @param {object|null} plan
   * @returns {void}
   */
  const showPlan = useCallback((plan) => {
    if (plan?.id) setActivePlanId(plan.id);
  }, []);

  /** Turns the pricing card to the free trial, when there is one. @returns {void} */
  const showTrial = useCallback(() => showPlan(trialPlan), [showPlan, trialPlan]);

  const closeCheckout = useCallback(() => setCheckoutOpen(false), []);

  return (
    <div className="kl">
      <PosLandingNav />

      <main id="top">
        <PosLandingHero
          trialLabel={trialLabel}
          entryPlan={entryPlan(plans)}
          bestSaving={bestSaving}
          onShowPlan={showPlan}
          onTryFree={showTrial}
        />
        <PosLandingProof />
        <PosLandingShowcase />
        <PosLandingCustom />
        <PosLandingPricing
          plans={togglePlans}
          loading={plansLoading}
          loadError={plansError}
          activeIndex={activeIndex}
          onSelect={(index) => showPlan(togglePlans[index])}
          trialLabel={trialLabel}
          addons={addons}
          onAddonsChange={setAddons}
          prices={prices}
          bestPercent={bestSaving?.percent || null}
          onCheckout={() => setCheckoutOpen(true)}
        />
        <PosLandingFaq prices={prices} />
        <PosLandingFinal trialLabel={trialLabel} onTryFree={showTrial} />
      </main>

      <PosLandingFooter />

      <PosLandingSubscribeModal
        open={checkoutOpen}
        plan={activePlan}
        addons={addons}
        prices={prices}
        onClose={closeCheckout}
      />
    </div>
  );
}

export default PosWelcomePage;
